import { useState } from 'react';
import type { SettlementHistoryItem } from '@fdh/shared';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  AutoTranslationHint,
  resolveSettlementRejectedReasonDisplay,
  resolveTranslationTargetLanguageLabel,
  settlementRejectedReasonHasTranslation,
} from '../../translation';
import {
  MODAL_BACKDROP_BOTTOM_CLASS,
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
} from '../../../lib/modalClasses';
import { ModalCircleCloseButton } from '../../ui/ModalCircleCloseButton';
import {
  formatEmployerHistorySettlementResult,
  formatEmployerMoneyAmount,
  resolveEmployerHistoryStatusClass,
  resolveEmployerHistoryStatusLabel,
} from '../../../utils/employerMoneyGivenSettlement';

type SettlementHistoryDetailModalProps = {
  item: SettlementHistoryItem;
  onClose: () => void;
  submittedAtLabel: string;
  eventAtLabel: string;
  moneyGivenAmountLabel: string;
  totalSpentLabel: string;
  settlementResultLabel: string;
  rejectReasonLabel: string;
  receiptsTitle: string;
  closeLabel: string;
};

export const SettlementHistoryDetailModal = ({
  item,
  onClose,
  submittedAtLabel,
  eventAtLabel,
  moneyGivenAmountLabel,
  totalSpentLabel,
  settlementResultLabel,
  rejectReasonLabel,
  receiptsTitle,
  closeLabel,
}: SettlementHistoryDetailModalProps) => {
  const { t, language } = useLanguage();
  const [showRejectedReasonOriginal, setShowRejectedReasonOriginal] = useState(false);
  const rejectedReasonTrimmed = item.rejectedReason.trim();
  const displayRejectedReason = resolveSettlementRejectedReasonDisplay(
    rejectedReasonTrimmed,
    item.translation,
    showRejectedReasonOriginal,
  );
  const showRejectReasonTranslation = settlementRejectedReasonHasTranslation(
    rejectedReasonTrimmed,
    item.translation,
  );
  const balanceClass =
    item.balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  const statusLabel = resolveEmployerHistoryStatusLabel(item.status, t);
  const statusClass = resolveEmployerHistoryStatusClass(item.status);

  return (
    <div
      className={`${MODAL_BACKDROP_BOTTOM_CLASS} z-[60] ${MODAL_BACKDROP_SCRIM_CLASS}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settlement-history-modal-title"
        className={`w-full max-w-md rounded-xl bg-white p-5 shadow-lg dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h3 id="settlement-history-modal-title" className={`min-w-0 flex-1 text-lg font-semibold ${statusClass}`}>
            {statusLabel}
          </h3>
          <ModalCircleCloseButton onClick={onClose} ariaLabel={closeLabel} />
        </div>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-600 dark:text-gray-400">{eventAtLabel}</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">{item.eventAt}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-600 dark:text-gray-400">{submittedAtLabel}</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">{item.submittedAt}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-600 dark:text-gray-400">{totalSpentLabel}</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {formatEmployerMoneyAmount(item.totalSpent)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-600 dark:text-gray-400">{moneyGivenAmountLabel}</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {formatEmployerMoneyAmount(item.budgetAmount)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-600 dark:text-gray-400">{settlementResultLabel}</dt>
            <dd className={`font-semibold ${balanceClass}`}>
              {formatEmployerHistorySettlementResult(item.balance, t)}
            </dd>
          </div>
        </dl>
        {item.status === 'rejected' && rejectedReasonTrimmed !== '' ? (
          <div className="mt-4 rounded-lg border border-red-100 bg-red-50 p-3 dark:border-red-900/40 dark:bg-red-950/30">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold text-red-800 dark:text-red-200">{rejectReasonLabel}</p>
              {showRejectReasonTranslation ? (
                <AutoTranslationHint
                  targetLanguageLabel={resolveTranslationTargetLanguageLabel(
                    t,
                    item.translation?.targetLanguage,
                    language,
                  )}
                  showingOriginal={showRejectedReasonOriginal}
                  onToggleOriginal={() => setShowRejectedReasonOriginal((prev) => !prev)}
                />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-red-900 dark:text-red-100">{displayRejectedReason}</p>
          </div>
        ) : null}
        <section className="mt-4" aria-labelledby="settlement-history-receipts-heading">
          <h4 id="settlement-history-receipts-heading" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {receiptsTitle}
          </h4>
          {item.receipts.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">—</p>
          ) : (
            <ul className="mt-2 space-y-2" role="list">
              {item.receipts.map((receipt) => (
                <li
                  key={receipt.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 dark:text-gray-300">{receipt.receiptDate}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {formatEmployerMoneyAmount(receipt.totalAmount)}
                    </span>
                  </div>
                  {receipt.company ? (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{receipt.company}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};
